import { Link } from 'react-router'
import { Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Call } from '../../api/telemetry'
import { StatusBadge } from '../shared/StatusBadge'
import { DirectionBadge } from '../shared/DirectionBadge'
import { formatPhone, formatDuration, formatDateTime } from '../../lib/utils'

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation('calls')
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="ml-2 inline-flex items-center text-text-muted hover:text-primary transition-colors"
      title={t('detail.metadata.copyToClipboard')}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="text-sm text-text-primary">{children}</dd>
    </div>
  )
}

export function CallMetadata({ call }: { call: Call }) {
  const { t } = useTranslation('calls')
  return (
    <div className="rounded-xl border border-border bg-surface-elevated p-6">
      <h2 className="mb-4 text-lg font-semibold text-text-primary">{t('detail.metadata.title')}</h2>
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t('detail.metadata.callSid')}>
          <span className="inline-flex items-center">
            <code className="max-w-[220px] truncate font-mono text-xs">{call.call_sid}</code>
            <CopyButton text={call.call_sid} />
          </span>
        </Field>

        <Field label={t('detail.metadata.direction')}>
          <DirectionBadge direction={call.direction} />
        </Field>

        <Field label={t('detail.metadata.mode')}>
          <span className="capitalize">{call.mode}</span>
        </Field>

        <Field label={t('detail.metadata.status')}>
          <StatusBadge status={call.status} />
        </Field>

        <Field label={t('detail.metadata.fromTo')}>
          <span>
            <Link to={`/app/calls/stage/${encodeURIComponent(call.from_number)}`} className="text-primary hover:text-primary-hover">
              {formatPhone(call.from_number)}
            </Link>
            <span className="mx-1 text-text-muted">→</span>
            <Link to={`/app/calls/stage/${encodeURIComponent(call.to_number)}`} className="text-primary hover:text-primary-hover">
              {formatPhone(call.to_number)}
            </Link>
          </span>
        </Field>

        <Field label={t('detail.metadata.startedAt')}>
          {formatDateTime(call.started_at)}
        </Field>

        <Field label={t('detail.metadata.endedAt')}>
          {call.ended_at ? formatDateTime(call.ended_at) : '—'}
        </Field>

        <Field label={t('detail.metadata.duration')}>
          {formatDuration(call.duration_s)}
        </Field>

        {call.ended_by && (
          <Field label={t('detail.metadata.endedBy')}>
            <span className="capitalize">{call.ended_by}</span>
          </Field>
        )}

        {call.ended_reason && (
          <Field label={t('detail.metadata.endedReason')}>
            {call.ended_reason}
          </Field>
        )}

        {/* Assistant id stamped at outbound creation / inbound assistant
            request. Audit-transparency surface for the per-org phone
            assignment work — operators can see which assistant actually
            handled the call even after the org's bindings change. We render
            the raw VAPI uuid (no name lookup yet) — it's already truncatable
            via the same monospace style as call_sid. */}
        <Field label={t('detail.metadata.assistant')}>
          {call.assistant_id_used ? (
            <span className="inline-flex items-center">
              <code className="max-w-[220px] truncate font-mono text-xs">{call.assistant_id_used}</code>
              <CopyButton text={call.assistant_id_used} />
            </span>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </Field>

        {/* Cost / charge is now shown in CostBreakdownPanel (superadmin)
            or CallChargePanel (non-superadmin). */}
      </dl>
    </div>
  )
}
